import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ShieldQuestion, Loader2 } from 'lucide-react'
import type { InvoiceVerification } from '@toga/shared'
import { formatCents } from '../lib/money'
import { useTheme } from '../theme/ThemeContext'

/**
 * Página PÚBLICA de verificación (destino del QR impreso en las facturas).
 *
 * Va fuera de AuthGuard: quien la abre es el cliente que ha recibido la
 * factura, y no tiene cuenta en el CRM. Muestra únicamente lo que devuelve
 * /public/invoice-verification — datos del emisor y de la propia factura, nada
 * del destinatario (ver el razonamiento en la ruta del backend).
 */
export default function VerifyInvoice() {
  const { t } = useTranslation('common')
  const { theme } = useTheme()
  const [params] = useSearchParams()
  const csv = (params.get('csv') ?? '').trim()
  const [state, setState] = useState<'loading' | 'done' | 'error'>('loading')
  const [data, setData] = useState<InvoiceVerification | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!csv) {
      setState('done')
      setData({ found: false })
      return
    }
    // fetch directo en vez del cliente `api`: esta página es anónima y no debe
    // arrastrar el manejo de sesión/CSRF ni disparar el redirect a /login.
    fetch(`/api/public/invoice-verification?csv=${encodeURIComponent(csv)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((json: InvoiceVerification) => {
        if (cancelled) return
        setData(json)
        setState('done')
      })
      .catch(() => {
        if (cancelled) return
        setState('error')
      })
    return () => {
      cancelled = true
    }
  }, [csv])

  const isLight = theme === 'light'
  const found = state === 'done' && data?.found === true

  const row = (label: string, value: string | null | undefined) =>
    value ? (
      <div className="flex justify-between gap-4 py-2 border-b border-base last:border-0">
        <span className="text-sm t-muted shrink-0">{label}</span>
        <span className="text-sm t-primary text-right break-all">{value}</span>
      </div>
    ) : null

  const fmtDate = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'long' }) : null

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 font-heebo t-primary relative">
      <div className="fixed inset-0 -z-10 bg-page" />

      <div className="w-full max-w-lg surface p-6 md:p-8 animate-slideUp">
        <div className="flex flex-col items-center mb-6 text-center">
          <img
            src={isLight ? '/logo-mark.svg' : '/logo-mark-light.svg'}
            alt="JR2"
            draggable={false}
            className="h-8 w-auto select-none mb-4"
          />
          <h1 className="text-lg font-semibold t-primary">{t('verify.title')}</h1>
        </div>

        {state === 'loading' && (
          <div className="flex items-center justify-center gap-2 py-8 t-muted">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">{t('verify.checking')}</span>
          </div>
        )}

        {state === 'error' && (
          <p className="text-sm t-secondary text-center py-8">{t('verify.errorGeneric')}</p>
        )}

        {state === 'done' && !found && (
          <div className="flex flex-col items-center text-center py-6 gap-3">
            <ShieldQuestion size={32} className="t-muted" />
            <p className="text-sm t-secondary">{t('verify.notFound')}</p>
            {csv && <p className="text-xs t-muted font-mono break-all">CSV: {csv}</p>}
          </div>
        )}

        {found && data && (
          <>
            <div className="flex items-center gap-2 justify-center mb-5 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={20} />
              <span className="text-sm font-semibold">{t('verify.valid')}</span>
            </div>

            <div className="surface-soft px-4 py-2 mb-4">
              {row(t('verify.issuer'), data.issuerName)}
              {row(t('verify.issuerNationalId'), data.issuerNationalId)}
              {row(t('verify.number'), data.number)}
              {row(t('verify.issueDate'), fmtDate(data.issueDate))}
              {row(t('verify.base'), formatCents(data.subtotalCents))}
              {row(t('verify.vat'), formatCents(data.vatCents))}
              {data.irpfCents ? row(t('verify.irpf'), formatCents(-data.irpfCents)) : null}
              {row(t('verify.total'), formatCents(data.totalCents))}
            </div>

            <div className="surface-soft px-4 py-2">
              {row('CSV', data.csv)}
              {row(t('verify.hash'), data.hash)}
            </div>
          </>
        )}

        <p className="text-xs t-muted mt-6 leading-relaxed text-center">
          {t('verify.privacyNote')}
        </p>
      </div>
    </div>
  )
}
